import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2, Camera, Filter, X, FileText, Download, ArrowLeft, Trash2, Send, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

const STATUS_COLORS = {
  sent: 'bg-green-500/20 text-green-400',
  pending: 'bg-amber-500/20 text-amber-400',
  failed: 'bg-red-500/20 text-red-400',
  not_required: 'bg-white/10 text-gray-300',
};

const TYPE_LABELS = {
  project: 'Project',
  multi_project: 'Multi-Project',
  general: 'General Overhead',
};

function formatUKDate(isoStr) {
  if (!isoStr) return '-';
  try {
    const d = new Date(isoStr);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  } catch { return isoStr.slice(0, 16); }
}

export default function AllCaptures() {
  const { api, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [captures, setCaptures] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [users, setUsers] = useState([]);

  const [status, setStatus] = useState('all');
  const [company, setCompany] = useState('all');
  const [destination, setDestination] = useState('all');
  const [captureType, setCaptureType] = useState('all');
  const [createdBy, setCreatedBy] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Per-row delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null); // capture object pending deletion
  const [deleting, setDeleting] = useState(false);

  // Resend (single row / all unsent)
  const [resendingId, setResendingId] = useState(null);
  const [resendingAll, setResendingAll] = useState(false);
  const [unsentCount, setUnsentCount] = useState(0);

  useEffect(() => {
    if (!isAdmin) { navigate('/portal/my-captures'); return; }
    api.get('/staff').then(res => setUsers(res.data || [])).catch(() => {});
  }, [isAdmin, navigate, api]);

  const buildParams = useCallback((offset = 0) => {
    const params = new URLSearchParams();
    if (status !== 'all') params.set('status', status);
    if (company !== 'all') params.set('company', company);
    if (destination !== 'all') params.set('destination', destination);
    if (captureType !== 'all') params.set('type', captureType);
    if (createdBy !== 'all') params.set('created_by', createdBy);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    params.set('limit', '50');
    params.set('offset', String(offset));
    return params.toString();
  }, [status, company, destination, captureType, createdBy, dateFrom, dateTo]);

  const loadCaptures = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/captures/all-captures?${buildParams(0)}`);
      setCaptures(res.data.captures || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load captures');
    } finally {
      setLoading(false);
    }
  }, [api, buildParams]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await api.get(`/captures/all-captures?${buildParams(captures.length)}`);
      setCaptures(prev => [...prev, ...(res.data.captures || [])]);
    } catch {
      toast.error('Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => { if (isAdmin) loadCaptures(); }, [isAdmin, loadCaptures]);

  const loadUnsentCount = useCallback(async () => {
    try {
      const res = await api.get('/captures/all-captures?status=unsent&limit=1&offset=0');
      setUnsentCount(res.data.total || 0);
    } catch { /* badge only */ }
  }, [api]);

  useEffect(() => { if (isAdmin) loadUnsentCount(); }, [isAdmin, loadUnsentCount]);

  const applyResendResult = (r) => {
    setCaptures(prev => prev.map(c => c.id === r.id ? { ...c, sent_status: r.sent_status, failure_reason: r.failure_reason, message_id: r.message_id } : c));
  };

  const handleResend = async (c) => {
    setResendingId(c.id);
    try {
      const res = await api.post(`/captures/${c.id}/resend`);
      applyResendResult(res.data);
      if (res.data.sent_status === 'sent') toast.success(`Sent to ${res.data.destination_email || 'destination'}`);
      else toast.error(res.data.failure_reason || 'Still not sent');
      loadUnsentCount();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Resend failed';
      toast.error(detail);
      setCaptures(prev => prev.map(x => x.id === c.id ? { ...x, sent_status: 'failed', failure_reason: detail } : x));
    } finally {
      setResendingId(null);
    }
  };

  const handleResendAll = async () => {
    setResendingAll(true);
    try {
      const res = await api.post('/captures/resend-unsent');
      (res.data.results || []).forEach(applyResendResult);
      if (res.data.attempted === 0) toast.info('Nothing to resend');
      else if (res.data.failed === 0) toast.success(`${res.data.sent} capture${res.data.sent === 1 ? '' : 's'} sent`);
      else toast.warning(`${res.data.sent} sent · ${res.data.failed} still not sent`);
      setUnsentCount(res.data.still_unsent ?? 0);
      if (status === 'unsent') loadCaptures();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Resend failed');
    } finally {
      setResendingAll(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (company !== 'all') params.set('company', company);
      if (captureType !== 'all') params.set('capture_type', captureType);
      if (destination !== 'all') params.set('destination_type', destination);
      if (status !== 'all') params.set('status', status);
      if (dateFrom) params.set('start_date', dateFrom);
      if (dateTo) params.set('end_date', dateTo);
      const res = await api.get(`/captures/export?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `captures_${format(new Date(), 'yyyyMMdd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch { toast.error('Export failed'); }
  };

  const clearFilters = () => {
    setStatus('all'); setCompany('all'); setDestination('all');
    setCaptureType('all'); setCreatedBy('all'); setDateFrom(''); setDateTo('');
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/captures/${deleteTarget.id}`);
      // Optimistic UI: remove from list immediately
      setCaptures(prev => prev.filter(c => c.id !== deleteTarget.id));
      setTotal(t => Math.max(0, t - 1));
      toast.success('Capture deleted');
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const hasFilters = status !== 'all' || company !== 'all' || destination !== 'all' || captureType !== 'all' || createdBy !== 'all' || dateFrom || dateTo;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto" data-testid="all-captures-page">
      <button
        onClick={() => navigate('/captures')}
        className="flex items-center gap-1 text-gray-400 hover:text-white mb-4 text-sm"
        data-testid="back-to-new-capture"
      >
        <ArrowLeft className="w-4 h-4" /> Back to New Capture
      </button>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Camera className="w-6 h-6 text-[#F5A623]" /> All Captures
          </h1>
          <p className="text-sm text-gray-400">{total} capture{total !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          {unsentCount > 0 && (
            <Button
              onClick={handleResendAll}
              disabled={resendingAll}
              className="bg-amber-500/20 text-amber-300 border border-amber-500/40 hover:bg-amber-500/30"
              data-testid="resend-all-unsent-btn"
            >
              {resendingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Resend {unsentCount} not sent
            </Button>
          )}
          <Button onClick={handleExport} variant="outline" className="border-white/20 text-gray-300 hover:bg-white/5" data-testid="export-btn">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-6 items-end">
        <Select value={createdBy} onValueChange={setCreatedBy}>
          <SelectTrigger className="w-[160px] bg-[#2A3240] border-white/20 text-white text-xs h-9" data-testid="filter-user">
            <SelectValue placeholder="User" />
          </SelectTrigger>
          <SelectContent className="bg-[#2A3240] border-white/10">
            <SelectItem value="all" className="text-white">All Users</SelectItem>
            {users.map(u => (
              <SelectItem key={u.id} value={u.id} className="text-white">{u.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[120px] bg-[#2A3240] border-white/20 text-white text-xs h-9" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#2A3240] border-white/10">
            <SelectItem value="all" className="text-white">All Status</SelectItem>
            <SelectItem value="unsent" className="text-amber-300" data-testid="filter-status-unsent">Not sent</SelectItem>
            <SelectItem value="sent" className="text-white">Sent</SelectItem>
            <SelectItem value="failed" className="text-white">Failed</SelectItem>
            <SelectItem value="pending" className="text-white">Pending</SelectItem>
          </SelectContent>
        </Select>
        <Select value={company} onValueChange={setCompany}>
          <SelectTrigger className="w-[110px] bg-[#2A3240] border-white/20 text-white text-xs h-9"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#2A3240] border-white/10">
            <SelectItem value="all" className="text-white">All</SelectItem>
            <SelectItem value="CON" className="text-white">CON</SelectItem>
            <SelectItem value="DEV" className="text-white">DEV</SelectItem>
          </SelectContent>
        </Select>
        <Select value={destination} onValueChange={setDestination}>
          <SelectTrigger className="w-[110px] bg-[#2A3240] border-white/20 text-white text-xs h-9"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#2A3240] border-white/10">
            <SelectItem value="all" className="text-white">All</SelectItem>
            <SelectItem value="livecosts" className="text-white">LiveCosts</SelectItem>
            <SelectItem value="xero" className="text-white">Xero</SelectItem>
          </SelectContent>
        </Select>
        <Select value={captureType} onValueChange={setCaptureType}>
          <SelectTrigger className="w-[110px] bg-[#2A3240] border-white/20 text-white text-xs h-9"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#2A3240] border-white/10">
            <SelectItem value="all" className="text-white">All Types</SelectItem>
            <SelectItem value="project" className="text-white">Project</SelectItem>
            <SelectItem value="multi_project" className="text-white">Multi-Proj</SelectItem>
            <SelectItem value="general" className="text-white">General</SelectItem>
          </SelectContent>
        </Select>
        <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-[130px] bg-[#2A3240] border-white/20 text-white text-xs h-9" />
        <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-[130px] bg-[#2A3240] border-white/20 text-white text-xs h-9" />
        {hasFilters && (
          <button onClick={clearFilters} className="text-xs text-[#F5A623] hover:underline flex items-center gap-1">
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#F5A623]" /></div>
      ) : captures.length === 0 ? (
        <Card className="bg-[#2A3240] border-white/10">
          <CardContent className="py-12 text-center">
            {hasFilters ? (
              <>
                <Filter className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No captures match these filters</p>
                <button onClick={clearFilters} className="text-[#F5A623] text-sm mt-2 hover:underline">Clear filters</button>
              </>
            ) : (
              <>
                <Camera className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No captures recorded yet</p>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="all-captures-table">
              <thead>
                <tr className="border-b border-white/10 text-gray-400 text-xs uppercase">
                  <th className="text-left py-3 px-3">Date</th>
                  <th className="text-left py-3 px-3">User</th>
                  <th className="text-left py-3 px-3">Type</th>
                  <th className="text-left py-3 px-3">Company</th>
                  <th className="text-left py-3 px-3">Project</th>
                  <th className="text-left py-3 px-3">Dest</th>
                  <th className="text-left py-3 px-3">Status</th>
                  <th className="text-left py-3 px-3">PDF</th>
                  <th className="text-right py-3 px-3 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {captures.map(c => (
                  <tr key={c.id} className="border-b border-white/5 hover:bg-white/5 cursor-pointer" onClick={() => navigate(`/portal/my-captures/${c.id}`)}>
                    <td className="py-3 px-3 text-gray-300 text-xs">{formatUKDate(c.created_at)}</td>
                    <td className="py-3 px-3 text-white">{c.user_name}</td>
                    <td className="py-3 px-3 text-gray-300">{TYPE_LABELS[c.capture_type] || c.capture_type}</td>
                    <td className="py-3 px-3"><Badge className={`text-[10px] ${c.company === 'CON' ? 'bg-blue-500/20 text-blue-400' : 'bg-orange-500/20 text-orange-400'}`}>{c.company}</Badge></td>
                    <td className="py-3 px-3 text-gray-300 text-xs">{c.linked_project_ref || '-'}</td>
                    <td className="py-3 px-3 text-gray-400 text-xs capitalize">{c.destination_type}</td>
                    <td className="py-3 px-3">
                      <Badge
                        className={`${STATUS_COLORS[c.sent_status]} text-[10px] ${c.failure_reason ? 'cursor-help' : ''}`}
                        title={c.sent_status !== 'sent' ? (c.failure_reason || undefined) : undefined}
                        data-testid={`status-badge-${c.id}`}
                      >
                        {c.details_only ? 'details only' : c.sent_status === 'sent' ? 'sent' : c.processing ? 'sending…' : 'not sent'}
                      </Badge>
                    </td>
                    <td className="py-3 px-3">{c.output_pdf?.onedrive_url ? <a href={c.output_pdf.onedrive_url} target="_blank" rel="noopener noreferrer" className="text-[#F5A623] text-xs hover:underline" onClick={e => e.stopPropagation()}>View</a> : '-'}</td>
                    <td className="py-3 px-3 text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                      {c.sent_status !== 'sent' && !c.processing && !c.details_only && (
                        <button
                          onClick={() => handleResend(c)}
                          disabled={resendingId === c.id || resendingAll}
                          className="text-amber-400 hover:text-amber-300 disabled:opacity-50 transition-colors p-1 mr-1"
                          title={c.pdf_path ? 'Resend to destination' : 'No stored PDF — captured before resend was available'}
                          data-testid={`resend-capture-${c.id}`}
                        >
                          {resendingId === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        </button>
                      )}
                      <button
                        onClick={() => setDeleteTarget(c)}
                        className="text-gray-500 hover:text-red-400 transition-colors p-1"
                        title="Delete capture"
                        data-testid={`delete-capture-${c.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {captures.length < total && (
            <Button onClick={loadMore} disabled={loadingMore} variant="outline" className="w-full mt-4 border-white/10 text-gray-400 hover:bg-white/5" data-testid="load-more-btn">
              {loadingMore ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Load more ({total - captures.length} remaining)
            </Button>
          )}
        </>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent className="bg-[#2A3240] border-white/10 text-white" data-testid="delete-capture-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete this capture?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              {deleteTarget && (
                <span className="block space-y-1">
                  <span className="block">
                    <span className="text-gray-500">Job:</span>{' '}
                    <span className="text-white">{deleteTarget.linked_project_ref || TYPE_LABELS[deleteTarget.capture_type] || deleteTarget.capture_type}</span>
                  </span>
                  <span className="block">
                    <span className="text-gray-500">Destination:</span>{' '}
                    <span className="text-white">{deleteTarget.destination_type} ({deleteTarget.company})</span>
                  </span>
                  <span className="block">
                    <span className="text-gray-500">Date:</span>{' '}
                    <span className="text-white">{formatUKDate(deleteTarget.created_at)}</span>
                  </span>
                  <span className="block mt-3 text-amber-300/80 text-xs">
                    This removes the record from Lux Opps. Already-delivered emails and OneDrive PDFs are unaffected.
                  </span>
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deleting}
              className="bg-transparent border-white/20 text-gray-300 hover:bg-white/5"
              data-testid="delete-cancel-btn"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={handleConfirmDelete}
              className="bg-red-500 text-white hover:bg-red-600"
              data-testid="delete-confirm-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
